import { AuxButton, AuxGridColumnType, AuxTabBar } from "@blk/aladdin-react-components-es";
import { AuxButtonSizeEnum } from "@blk/aladdin-web-components";
import { push } from "connected-react-router";
import { isEqual } from "lodash";
import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSetPMIMenuItem } from "../use-menu-items";
import { InsDeleteBenchmarksAction } from "./insDeleteBenchmarkAction";
import { EntityType, MarketType, useGetMarkets } from "api/admin/src";
import { exportName } from "common/constants/exportName";
import { InsBulkPMISelectionMenu } from "components/frame/insBulkPMISelectionMenu";
import { ModalName } from "components/modal/types";
import { InsGridWidget } from "components/widgets";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import usePageTitle from "hooks/use-page-title";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setMarketsTab } from "store/grid-settings";
import { setSelectedRowsState } from "store/grids";
import { openModal } from "store/modal";
import { getCurrentLocalTime } from "common/date-utils";
const MarketsTabs = {
    Blended: "blended",
    Standard: "standard",
    UserDefined: "userDefined"
};
export const insMarketsstateId = "all-markets-grid";
const allowedStandardSources = [
    language.en.administration.marketDataAdministration.indices.defaultInsightResearchSource,
    language.en.markets.PreqinIndex
];
export const StandardMarketListColDefs = [
    {
        field: "name",
        headerName: language.en.markets.gridHeaderField.benchmarkName,
        type: [CustomColumnType.InsLinkColumn]
    },
    { field: "source", headerName: language.en.markets.gridHeaderField.source, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "numberOfFunds", headerName: language.en.markets.gridHeaderField.numberOfFunds, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "assetClass", headerName: language.en.markets.gridHeaderField.assetClass, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "strategy", headerName: language.en.markets.gridHeaderField.strategy, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "geography", headerName: language.en.markets.gridHeaderField.geography, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { cellDataType: false, field: "vintage", headerName: language.en.markets.gridHeaderField.vintageYear, type: [CustomColumnType.AUX_TEXT_COLUMN] }
];
export const BlendedOrUserDefinedMarketListColDefs = [
    {
        field: "name",
        headerName: language.en.markets.gridHeaderField.benchmarkName,
        type: [CustomColumnType.InsLinkColumn]
    },
    { field: "currency", headerName: language.en.markets.gridHeaderField.currency, maxWidth: 125, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "description", headerName: language.en.markets.gridHeaderField.description, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "createdBy", headerName: language.en.markets.gridHeaderField.createdBy, type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "createdAt", headerName: language.en.markets.gridHeaderField.createdAt, type: [CustomColumnType.AUX_DATE_COLUMN] }
];
const telemetryEventsGridClickedMap = {
    [MarketsTabs.Blended]: appInsightEvents.customEvents.names.BlendedBenchmarkGridClicked,
    [MarketsTabs.Standard]: appInsightEvents.customEvents.names.DefaultBenchmarkGridClicked,
    [MarketsTabs.UserDefined]: appInsightEvents.customEvents.names.UserDefinedBenchmarkGridClicked
};
const telemetryEventsOpenCreateModalMap = {
    [MarketsTabs.Blended]: appInsightEvents.customEvents.names.CreateBlendedBenchmarkOpenModal,
    [MarketsTabs.UserDefined]: appInsightEvents.customEvents.names.CreateUserDefinedBenchmarkOpenModal
};
const checkboxColumn = {
    field: "checkboxCol",
    floatingFilter: false,
    headerCheckboxSelection: true,
    headerName: " ",
    type: AuxGridColumnType.AUX_CHECKBOX_COLUMN,
    width: 32
};
const getColumnDefs = (tabSelected, settingsPMIsMode) => {
    const baseColDef = tabSelected === MarketsTabs.Standard ? StandardMarketListColDefs : BlendedOrUserDefinedMarketListColDefs;
    const shouldShowCheckbox = tabSelected === MarketsTabs.Standard ? settingsPMIsMode : true;
    return shouldShowCheckbox ? [checkboxColumn, ...baseColDef] : [...baseColDef];
};
const isVisibleInSelectedTab = (row, tabSelected) => {
    if (row.marketType !== tabSelected) {
        return false;
    }
    if (tabSelected !== MarketsTabs.Standard) {
        return true;
    }
    return Boolean(row.source && allowedStandardSources.includes(row.source));
};
const transformRowData = (data, tabSelected, dispatch) => data.filter((row) => isVisibleInSelectedTab(row, tabSelected)).map((row) => ({
    ...row,
    name: {
        label: row.name,
        onclick: () => dispatch(push(`market/${row.id}`))
    },
    vintage: Number(row?.vintage || 0) === 0
        ? language.en.administration.marketDataAdministration.indices.zeroVintageType
        : row?.vintage
}));
const handleTabSelection = (event, dispatch, setTabSelected) => {
    dispatch(setSelectedRowsState({
        id: insMarketsstateId,
        selectedRows: []
    }));
    setTabSelected(event.detail.tab.uid);
    dispatch(setMarketsTab(event.detail.tab.uid));
    appInsightsTrackEvent({ name: telemetryEventsGridClickedMap[event.detail.tab.uid], properties: { LocalTimestamp: getCurrentLocalTime() } });
};
const handleRowSelection = (e, dispatch) => {
    dispatch(setSelectedRowsState({
        id: insMarketsstateId,
        selectedRows: e.api.getSelectedRows().map((row) => `${row.id}`)
    }));
};
const handleCreateBenchmark = (tabSelected, dispatch) => {
    appInsightsTrackEvent({ name: telemetryEventsOpenCreateModalMap[tabSelected] });
    dispatch(openModal({
        content: {
            id: "benchmark-creation",
            modalName: ModalName.BenchmarkCreation,
            parameters: { mode: tabSelected }
        },
        title: tabSelected === MarketsTabs.UserDefined
            ? language.en.modal.benchmarkCreation.title
            : language.en.modal.benchmarkCreation.titleBlended,
        type: "action"
    }));
};
const renderAdditionalNodes = (tabSelected, refresh, dispatch) => {
    const isStandardTab = tabSelected === MarketsTabs.Standard;
    const buttonLabel = tabSelected === MarketsTabs.UserDefined
        ? language.en.modal.benchmarkCreation.title
        : language.en.modal.benchmarkCreation.titleBlended;
    return (React.createElement(Fragment, { key: "portfolios-additional-nodes" },
        React.createElement("span", { hidden: isStandardTab },
            React.createElement(InsDeleteBenchmarksAction, { refresh: refresh, id: insMarketsstateId, selectedRows: [] })),
        React.createElement("span", { hidden: isStandardTab },
            React.createElement(AuxButton, { size: AuxButtonSizeEnum.SMALL, onClick: () => handleCreateBenchmark(tabSelected, dispatch), label: buttonLabel }))));
};
export const InsMarkets = () => {
    usePageTitle(`${language.en.common.titles.research} - ${language.en.common.titles.base}`);
    const dispatch = useDispatch();
    const [tabSelected, setTabSelected] = useState(MarketsTabs.Standard);
    const settingsPMIsMode = useSelector((state) => state.mainGridSettings.setPMIsMode, isEqual);
    const exportResearchName = `${exportName.Research}_${tabSelected}`;
    const [data, querystate, refresh] = useGetMarkets({ marketTypes: [MarketType.Standard, MarketType.Blended, MarketType.UserDefined] });
    const colDef = getColumnDefs(tabSelected, settingsPMIsMode);
    const rows = transformRowData(data, tabSelected, dispatch);
    return React.createElement(React.Fragment, null,
        React.createElement("div", { style: { marginBottom: "-1px" } },
            React.createElement(AuxTabBar, { onTabBarItemSelected: (event) => handleTabSelection(event, dispatch, setTabSelected), tabType: "classic", activeUid: tabSelected, data: [
                    { label: language.en.markets.Standard, uid: MarketsTabs.Standard },
                    { label: language.en.markets.RuleBased, uid: MarketsTabs.UserDefined },
                    { label: language.en.markets.Blended, uid: MarketsTabs.Blended }
                ] })),
        React.createElement(InsGridWidget, { suppressExcelExport: false, exportExcelName: exportResearchName, stateId: insMarketsstateId, rowData: rows, columnDefs: colDef, reduceHeight: true, refreshAction: refresh, queryState: querystate, dataMenu: [[useSetPMIMenuItem(insMarketsstateId)]], rowSelectedHandler: (e) => handleRowSelection(e, dispatch), selectionMode: "multiple", additionalNodes: [renderAdditionalNodes(tabSelected, refresh, dispatch)], headerLeft: settingsPMIsMode ? React.createElement(InsBulkPMISelectionMenu, { stateId: insMarketsstateId, entityType: EntityType.Market }) : React.createElement(React.Fragment, null) }));
};
